import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  TextField, 
  Button, 
  Box, 
  Typography, 
  Alert, 
  Paper, 
  LinearProgress,
  Card,
  CardContent
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

const UserActivationPage = () => {
  const { activateAccount } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [token, setToken] = useState(searchParams.get('token') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Password validation
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  useEffect(() => {
    if (!token) {
      setError('Invalid activation link. Please check your email for the correct activation link.');
    }
  }, [token]);

  useEffect(() => {
    // Validate password in real time
    setPasswordValidation({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    });
  }, [password]);

  const isPasswordValid = Object.values(passwordValidation).every(Boolean);
  const passwordsMatch = password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!token) {
      setError('Invalid activation token');
      setLoading(false);
      return;
    }

    if (!isPasswordValid) {
      setError('Password does not meet requirements');
      setLoading(false);
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const result = await activateAccount(token, password);
      setSuccess(true);
      
      // Redirect to dashboard after successful activation
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Account activation failed');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        bgcolor="background.default"
        p={2}
      >
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              Account Activated!
            </Typography>
            <Typography variant="body1" color="textSecondary" mb={3}>
              Your account has been successfully activated. You are now being redirected to the dashboard.
            </Typography>
            <LinearProgress sx={{ mt: 2 }} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      minHeight="100vh"
      bgcolor="background.default"
      p={2}
    >
      <Paper elevation={3} sx={{ maxWidth: 500, width: '100%', p: 4 }}>
        <Box textAlign="center" mb={3}>
          <PersonAddIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Activate Your Account
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Complete your account setup by creating a secure password
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Activation Token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            fullWidth
            margin="normal"
            disabled={searchParams.get('token')} // Disable if token from URL
            required
            helperText="This token was provided in your invitation email"
          />

          <TextField
            label="New Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
            helperText="Password must meet all requirements below"
          />

          {/* Password requirements */}
          <Box sx={{ mt: 1, mb: 2 }}>
            <Typography variant="caption" color="textSecondary" display="block">
              Password Requirements:
            </Typography>
            <Box sx={{ ml: 1 }}>
              {Object.entries({
                length: 'At least 8 characters',
                uppercase: 'One uppercase letter',
                lowercase: 'One lowercase letter', 
                number: 'One number',
                special: 'One special character'
              }).map(([key, text]) => (
                <Typography 
                  key={key}
                  variant="caption" 
                  display="block"
                  color={passwordValidation[key] ? 'success.main' : 'textSecondary'}
                  sx={{ 
                    textDecoration: passwordValidation[key] ? 'line-through' : 'none',
                    opacity: passwordValidation[key] ? 0.7 : 1
                  }}
                >
                  • {text}
                </Typography>
              ))}
            </Box>
          </Box>

          <TextField
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
            error={confirmPassword.length > 0 && !passwordsMatch}
            helperText={
              confirmPassword.length > 0 && !passwordsMatch 
                ? "Passwords do not match" 
                : ""
            }
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading || !isPasswordValid || !passwordsMatch || !token}
            sx={{ mt: 3, mb: 2 }}
          >
            {loading ? 'Activating Account...' : 'Activate Account'}
          </Button>
        </form>

        <Box textAlign="center" mt={3}>
          <Typography variant="caption" color="textSecondary">
            Having trouble? Contact your system administrator for assistance.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserActivationPage;
